import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { WarningIcon } from '@/app/common/icons/support'
import { cn } from '@/lib/utils'
import { academicTermsQueries } from '@/queries/academic_terms/queries'
import {
  GlobalAcademicTermsArray,
  ProgramTermsArray,
} from '@/queries/academic_terms/schema'
import { ProgramYearsProps } from '@/queries/program/schemas'
import { updatePurseAcademicTermType } from '@/queries/program_purses/requests'
import { PURSE_KEYS } from '@/queries/program_purses/keys'
import { ProgramPurseResource } from '@/queries/program_purses/schemas'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import DistributionperiodItem from '../../../_components/distribution-period-item'
import SetTermStartAndEndDateForm from '../select-award-dates-dialog/set-term-start-and-end-date-form'

const AcademicTermsOptions = z.object({
  termId: z.string().min(1, { message: 'Please select a term format' }),
})

type AcademicTermsOptions = z.infer<typeof AcademicTermsOptions>

interface AcademicTermsFormProps {
  programSettings: ProgramYearsProps
  programAcademicTerms: ProgramTermsArray
  globalAcademicTerms: GlobalAcademicTermsArray
  purse: ProgramPurseResource
  onSuccess: () => void
}

export default function AcademicTermsForm(props: AcademicTermsFormProps) {
  const {
    programSettings,
    programAcademicTerms,
    globalAcademicTerms,
    purse,
    onSuccess,
  } = props

  const [selectedAwardPeriodId, setSelectedAwardPeriodId] = useState<
    number | null
  >(null)
  const [formStep, setFormStep] = useState<
    'select-term-dates' | 'select-term-format'
  >('select-term-format')
  const queryClient = useQueryClient()

  const { toast } = useToast()

  const updatePurseAcademicTermTypeMutation = useMutation({
    mutationFn: async ({
      academicTermTypeId,
    }: {
      academicTermTypeId: number
    }) => {
      return await updatePurseAcademicTermType({
        programId: programSettings.id,
        purseId: purse.id,
        academicTermTypeId,
      })
    },
  })

  const academicTermsForm = useForm<AcademicTermsOptions>({
    resolver: zodResolver(AcademicTermsOptions),
    defaultValues: {
      termId: purse.academic_term_type_id?.toString() ?? '',
    },
  })

  function onSubmit(data: AcademicTermsOptions) {
    updatePurseAcademicTermTypeMutation.mutate(
      { academicTermTypeId: Number(data.termId) },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.refetchQueries({
              queryKey: PURSE_KEYS.PURSE_DETAILS(
                purse.id.toString(),
                purse.program_id
              ),
            }),
            queryClient.refetchQueries({
              queryKey: academicTermsQueries.getProgramPurseAcademicTerms(
                purse.program_id,
                purse.id
              ).queryKey,
            }),
          ])
          setFormStep('select-term-dates')
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        },
      }
    )
  }

  const hasCompletedAllTerms = programAcademicTerms.every((term) => {
    return term.awarding_period_start_date && term.awarding_period_end_date
  })

  if (formStep === 'select-term-dates') {
    return (
      <div className=" max-w-5xl w-full mx-auto">
        <Button
          className="h-10 w-10 mb-4"
          variant={'outline'}
          onClick={() => setFormStep('select-term-format')}
        >
          <ArrowLeftIcon className="size-4" />
        </Button>

        {/* Program Information Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">Program</div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {programSettings.name}
                </h2>
              </div>
              <div className="flex items-center gap-12">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Start Date</div>
                  <div className="text-sm font-medium text-gray-900">
                    {programSettings.program_start_date}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">End Date</div>
                  <div className="text-sm font-medium text-gray-900">
                    {programSettings.program_end_date}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="">
          <CardHeader>
            <div className="text-xl font-medium text-gray-800">
              Award Distribution Period
            </div>
            <p className="text-sm text-gray-600 font-normal mt-2 max-w-3xl">
              Set the start and end dates for when awards (scholarships, grants,
              financial aid) can be distributed during this term. Award seasons
              define the active period for financial aid disbursement and must
              fall within the term's overall date range.
            </p>
          </CardHeader>
          <CardContent className="py-10">
            <div className="flex flex-col justify-between gap-4  w-full">
              {programAcademicTerms.map((period) => {
                const hasAddedDates =
                  period.awarding_period_start_date &&
                  period.awarding_period_end_date

                return (
                  <DistributionperiodItem
                    key={period.id}
                    period={period}
                    actionContent={
                      <div>
                        {hasAddedDates ? (
                          <Button
                            onClick={() => setSelectedAwardPeriodId(period.id)}
                            variant={'outline'}
                          >
                            Edit Award Dates
                          </Button>
                        ) : (
                          <Button
                            onClick={() => setSelectedAwardPeriodId(period.id)}
                          >
                            Add Award Dates
                          </Button>
                        )}
                      </div>
                    }
                  />
                )
              })}
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            <Button
              onClick={() => setFormStep('select-term-format')}
              size={'lg'}
              variant={'outline'}
            >
              Back
            </Button>
            <Button
              onClick={onSuccess}
              disabled={!hasCompletedAllTerms}
              size={'lg'}
              variant={'primary'}
            >
              Continue
            </Button>
          </CardFooter>
        </Card>
        {selectedAwardPeriodId && (
          <SetTermStartAndEndDateForm
            selectedAwardPeriodId={selectedAwardPeriodId}
            onOpenChange={() => setSelectedAwardPeriodId(null)}
            programAcademicTerms={programAcademicTerms}
            programSettings={programSettings}
            purseId={purse.id}
          />
        )}
      </div>
    )
  }

  return (
    <div className="max-w-lg w-full mx-auto space-y-8 mt-20">
      <Card className="">
        <CardHeader>
          <div className="text-xl font-medium text-gray-800">
            Create Academic Term
          </div>
          <p className="text-sm text-gray-600 font-normal mt-2">
            Academic terms divide your school year into distinct periods that
            help organize student enrollment, course schedules, and financial
            aid disbursements.{' '}
          </p>
        </CardHeader>
        <CardContent>
          <Form {...academicTermsForm}>
            <form onSubmit={academicTermsForm.handleSubmit(onSubmit)}>
              <FormField
                name="termId"
                control={academicTermsForm.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium text-gray-700">
                      Select Your Academic Term System
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-4 mt-4"
                      >
                        {globalAcademicTerms.map((term) => {
                          const value = term.id.toString()
                          return (
                            <label
                              key={term.id.toString()}
                              htmlFor={term.id.toString()}
                              className={cn(
                                'flex items-center gap-4 p-5 border-2 rounded-lg cursor-pointer transition-all hover:border-primary-500',
                                field.value === value
                                  ? 'border-primary-500'
                                  : 'border-gray-200 bg-white'
                              )}
                            >
                              <RadioGroupItem
                                data-testid={term.name}
                                aria-label={term.name}
                                value={value}
                                id={term.id.toString()}
                              />
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">
                                  {term.name}
                                </div>
                              </div>
                            </label>
                          )
                        })}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <SubmitButton
                className="w-full mt-8"
                size="lg"
                type="submit"
                status={updatePurseAcademicTermTypeMutation.status}
                buttonTxt="Continue to Award Distribution"
              />
            </form>
          </Form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center gap-2">
          <WarningIcon className="text-yellow-500 text-2xl" />

          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-800">Important:</span> Once
            your first term begins, you won't be able to change the term system
            for the rest of the school year.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
